import { db, auth } from '../firebase.ts';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
  Timestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';

// API endpoints
/** Higher ports first: skip zombie listeners on 8101-8103 when Windows leaves ghost sockets. */
const LOCAL_API_PORTS = [8106, 8105, 8104, 8103, 8102, 8101];

function uniquePorts(ports: number[]): number[] {
  return [...new Set(ports.filter((p) => p >= 8101 && p <= 8106))];
}
const LOCAL_API_BASE_DEFAULT = 'http://localhost:8101';
const CLOUD_API_BASE = 'https://scraping-machine-469675949347.europe-west1.run.app';

const DASHBOARD_CANONICAL_HOST = 'spontane-content-manager.web.app';

let cachedLocalApiBase: string | null = null;

/** Attach a fresh Firebase ID token before admin Firestore reads/writes (deployed dashboard). */
async function ensureFirestoreAuth(): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not signed in. Please log in again.');
  }
  await user.getIdToken(true);
}

/** True when running on the canonical dashboard host or localhost (not on the PWA host). */
export function isDashboardOrigin(): boolean {
  const h = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  return h === 'localhost' || h === '127.0.0.1' || h === DASHBOARD_CANONICAL_HOST;
}

const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/** Try to find which port the local scraper server is on (8101, 8102, or 8103). */
export async function getLocalApiBase(forceRefresh = false): Promise<string> {
  if (forceRefresh) {
    cachedLocalApiBase = null;
  }
  if (cachedLocalApiBase) return cachedLocalApiBase;

  let preferredPid: number | null = null;
  let heartbeatPort: number | null = null;
  let portsToProbe = [...LOCAL_API_PORTS];
  try {
    const { getDoc } = await import('firebase/firestore');
    const [watcherSnap, hbSnap] = await Promise.all([
      getDoc(doc(db, 'local_server_status', 'watcher')),
      getDoc(doc(db, 'local_server_status', 'heartbeat')),
    ]);
    if (watcherSnap.exists()) {
      const wp = watcherSnap.data()?.server_pid;
      if (typeof wp === 'number') preferredPid = wp;
    }
    const hbPort = hbSnap.data()?.port;
    if (typeof hbPort === 'number') {
      heartbeatPort = hbPort;
      portsToProbe = uniquePorts([hbPort, ...LOCAL_API_PORTS]);
    }
  } catch {
    // Firestore unavailable from localhost — fall back to port probe only.
  }

  type AliveServer = { base: string; port: number; pid?: number };
  const alive: AliveServer[] = [];
  for (const port of portsToProbe) {
    try {
      const base = `http://localhost:${port}`;
      const r = await fetch(`${base}/ping`, { method: 'GET', signal: AbortSignal.timeout(3000) });
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        alive.push({ base, port: j.port ?? port, pid: j.pid });
      }
    } catch {
      continue;
    }
  }
  if (!alive.length) return '';

  if (preferredPid != null) {
    const match = alive.find((a) => a.pid === preferredPid);
    if (match) {
      cachedLocalApiBase = match.base;
      return match.base;
    }
  }

  if (heartbeatPort != null) {
    const hbMatch = alive.find((a) => a.port === heartbeatPort);
    if (hbMatch) {
      cachedLocalApiBase = hbMatch.base;
      return hbMatch.base;
    }
  }

  // Prefer highest port — newer instance after restart; skip ghost/stale 8101 when possible.
  alive.sort((a, b) => b.port - a.port);
  cachedLocalApiBase = alive[0].base;
  return alive[0].base;
}

function getLocalApiBaseSync(): string {
  return cachedLocalApiBase || LOCAL_API_BASE_DEFAULT;
}

function getApiBaseSync(): string {
  if (isLocal) return getLocalApiBaseSync();
  return CLOUD_API_BASE;
}

/**
 * Max wait when explicitly waiting for queue pickup (optional; manual runs use fast enqueue).
 */
const LOCAL_QUEUE_PICKUP_TIMEOUT_MS = 15 * 60 * 1000;

export interface LocalPendingQueueItem {
  id: string;
  scraper: string;
  requested_at?: string;
  trigger_type?: string;
  test_mode?: boolean;
  batch_id?: string;
}

export interface LocalTriggerResult {
  status: 'started' | 'queued' | 'accepted';
  scraper: string;
  run_id?: string;
  queue_id?: string;
  message?: string;
  already_queued?: boolean;
  started_at?: string;
  trigger_type?: string;
  local_server?: boolean;
}

/** Pending manual/scheduled jobs waiting for the single local scrape slot. */
export async function fetchLocalPendingQueue(): Promise<LocalPendingQueueItem[]> {
  try {
    await ensureFirestoreAuth();
    const q = query(
      collection(db, 'local_run_queue'),
      where('status', '==', 'pending'),
      orderBy('requested_at'),
      limit(50),
    );
    const snaps = await getDocs(q);
    return snaps.docs.map((snap) => {
      const data = snap.data();
      const requestedAt = data.requested_at;
      return {
        id: snap.id,
        scraper: String(data.scraper || ''),
        requested_at:
          requestedAt instanceof Timestamp
            ? requestedAt.toDate().toISOString()
            : typeof requestedAt === 'string'
              ? requestedAt
              : undefined,
        trigger_type: data.trigger_type,
        test_mode: data.test_mode === true,
        batch_id: typeof data.batch_id === 'string' ? data.batch_id : undefined,
      };
    });
  } catch (err) {
    console.warn('[LocalQueue] fetchLocalPendingQueue failed:', err);
    return [];
  }
}

/**
 * Queue a local run via Firestore — returns immediately (does not block until pickup).
 */
async function enqueueLocalRunViaFirestore(
  scraperName: string,
  testMode?: boolean,
  triggerType: 'manual_local' | 'scheduled_local' = 'manual_local',
): Promise<LocalTriggerResult> {
  await ensureFirestoreAuth();
  const docRef = await addDoc(collection(db, 'local_run_queue'), {
    scraper: scraperName,
    status: 'pending',
    requested_at: serverTimestamp(),
    trigger_type: triggerType,
    requested_by: 'dashboard',
    ...(testMode === true && { test_mode: true }),
  });
  return {
    status: 'queued',
    scraper: scraperName,
    queue_id: docRef.id,
    message: `${scraperName} queued — will start when the current local scrape finishes.`,
    trigger_type: triggerType,
    local_server: true,
  };
}

/**
 * Queue a local run and wait until the local server picks it up (legacy / optional).
 */
async function queueLocalRunViaFirestore(
  scraperName: string,
  testMode?: boolean,
  triggerType: 'manual_local' | 'scheduled_local' = 'manual_local',
): Promise<LocalTriggerResult> {
  const enqueued = await enqueueLocalRunViaFirestore(scraperName, testMode, triggerType);
  const queueId = enqueued.queue_id;
  if (!queueId) {
    return enqueued;
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsub();
      reject(new Error(
        `Local scraper server did not pick up "${scraperName}" within ${LOCAL_QUEUE_PICKUP_TIMEOUT_MS / 60000} minutes. ` +
        `Your job remains queued — refresh Live Progress to see pending jobs. ` +
        `Ensure local_scraper_server.py is running on your PC.`
      ));
    }, LOCAL_QUEUE_PICKUP_TIMEOUT_MS);

    const unsub = onSnapshot(doc(db, 'local_run_queue', queueId), (snap) => {
      const data = snap.data();
      if (!data) return;
      if (data.status === 'picked_up' || data.status === 'running' || data.status === 'completed' || data.status === 'error') {
        clearTimeout(timeoutId);
        unsub();
        resolve({
          status: 'accepted',
          scraper: scraperName,
          run_id: data.run_id || queueId,
          message: `Scraper ${scraperName} started locally via Firestore queue.`,
          started_at: data.picked_up_at instanceof Timestamp ? data.picked_up_at.toDate().toISOString() : new Date().toISOString(),
          trigger_type: triggerType,
          local_server: true,
        });
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Local server control via Firestore (works from deployed HTTPS dashboard)
// ---------------------------------------------------------------------------

/** Max age (ms) of heartbeat before we consider the server offline. */
const HEARTBEAT_STALE_MS = 25_000;

export interface LocalServerStartupError {
  active: boolean;
  code?: string;
  message?: string;
  instructions?: string;
  autoFixable?: boolean;
  zombiePorts?: number[];
}

export interface LocalServerStatus {
  alive: boolean;
  lastSeen: string | null;
  runningScrapers: Record<string, string>;
  runningCount: number;
  serverPort?: number | null;
  /** True if the watcher process is running (can receive start/stop/restart from dashboard) */
  watcherAlive: boolean;
  /** Set when the watcher/server could not start automatically */
  startupError?: LocalServerStartupError | null;
  /** True when Firestore rules block local_server_* reads/writes (server may still be running on localhost). */
  firestorePermissionDenied?: boolean;
}

function isFirestorePermissionError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message || err || '').toLowerCase();
  const code = String((err as { code?: string })?.code || '').toLowerCase();
  return (
    code === 'permission-denied'
    || msg.includes('missing or insufficient permissions')
    || msg.includes('permission_denied')
  );
}

async function probeLocalServerViaPing(): Promise<{ alive: boolean; port?: number }> {
  const base = await getLocalApiBase(true);
  if (!base) return { alive: false };
  try {
    const response = await fetch(`${base}/ping`, { method: 'GET', signal: AbortSignal.timeout(3000) });
    if (!response.ok) return { alive: false };
    const body = await response.json().catch(() => ({}));
    return { alive: true, port: typeof body.port === 'number' ? body.port : undefined };
  } catch {
    return { alive: false };
  }
}

/**
 * Check if the local scraper server is running by reading its Firestore heartbeat.
 * Works from any origin (no localhost needed).
 */
export async function checkLocalServerRunning(): Promise<LocalServerStatus> {
  const empty: LocalServerStatus = {
    alive: false,
    lastSeen: null,
    runningScrapers: {},
    runningCount: 0,
    watcherAlive: false,
    startupError: null,
  };
  try {
    await ensureFirestoreAuth();
    const { getDoc } = await import('firebase/firestore');
    const [serverSnap, watcherSnap, errSnap] = await Promise.all([
      getDoc(doc(db, 'local_server_status', 'heartbeat')),
      getDoc(doc(db, 'local_server_status', 'watcher')),
      getDoc(doc(db, 'local_server_status', 'startup_error')),
    ]);
    const data = serverSnap.exists() ? serverSnap.data() : null;
    const lastSeen = data?.last_seen as string | null;
    const age = lastSeen ? Date.now() - new Date(lastSeen).getTime() : Infinity;
    const alive = !!data?.alive && age < HEARTBEAT_STALE_MS;
    const watcherData = watcherSnap.exists() ? watcherSnap.data() : null;
    const watcherLastSeen = watcherData?.last_seen as string | null;
    const watcherAge = watcherLastSeen ? Date.now() - new Date(watcherLastSeen).getTime() : Infinity;
    const watcherAlive = !!watcherData?.alive && watcherAge < HEARTBEAT_STALE_MS;
    const errData = errSnap.exists() ? errSnap.data() : null;
    const startupError: LocalServerStartupError | null =
      errData?.active
        ? {
            active: true,
            code: errData.code as string | undefined,
            message: errData.message as string | undefined,
            instructions: errData.instructions as string | undefined,
            autoFixable: !!errData.auto_fixable,
            zombiePorts: Array.isArray(errData.zombie_ports)
              ? (errData.zombie_ports as number[])
              : undefined,
          }
        : null;
    console.debug('[LocalServer] heartbeat doc exists:', serverSnap.exists(),
      '| alive:', data?.alive, '| last_seen:', lastSeen,
      '| age_ms:', age, '| stale_limit:', HEARTBEAT_STALE_MS,
      '| result_alive:', alive);
    console.debug('[LocalServer] watcher doc exists:', watcherSnap.exists(),
      '| alive:', watcherData?.alive, '| last_seen:', watcherLastSeen,
      '| age_ms:', watcherAge, '| result_alive:', watcherAlive);
    return {
      alive,
      lastSeen,
      runningScrapers: (data?.running_scrapers as Record<string, string>) || {},
      runningCount: (data?.running_count as number) || 0,
      serverPort: typeof data?.port === 'number' ? data.port : null,
      watcherAlive,
      startupError: alive ? null : startupError,
    };
  } catch (err) {
    console.error('[LocalServer] checkLocalServerRunning FAILED:', err);
    const firestorePermissionDenied = isFirestorePermissionError(err);
    if (firestorePermissionDenied && isLocal) {
      const probe = await probeLocalServerViaPing();
      if (probe.alive) {
        return {
          alive: true,
          lastSeen: null,
          runningScrapers: {},
          runningCount: 0,
          serverPort: probe.port ?? null,
          watcherAlive: false,
          startupError: null,
          firestorePermissionDenied: true,
        };
      }
    }
    return { ...empty, firestorePermissionDenied };
  }
}

/**
 * Client wait for Firestore command completion. Must exceed watcher worst case:
 * poll interval (3s) + stop_server() (terminate + wait 10s + kill + wait 5s) ≈ 18s+ for stop;
 * kill_and_restart adds sleep(1) and start.
 */
const LOCAL_SERVER_CMD_TIMEOUT_MS: Record<
  'start' | 'stop' | 'kill_and_restart' | 'run_full_automatic' | 'stop_scraping',
  number
> = {
  // Start must cover Firestore round-trip + watcher poll + Popen; 504s on write need margin.
  start: 60_000,
  stop: 50_000,
  kill_and_restart: 65_000,
  run_full_automatic: 90_000,
  stop_scraping: 60_000,
};

export interface LocalServerCommandResult {
  result: string;
  /** Present when command was `run_full_automatic` and scrapers were queued. */
  batch_id?: string | null;
}

function formatLocalServerCommandError(err: unknown, command: string): Error {
  if (isFirestorePermissionError(err)) {
    return new Error(
      'Firestore rules are out of date (Missing or insufficient permissions). '
      + 'Dashboard controls need `firebase deploy --only firestore:rules` from the repo root. '
      + `Command "${command}" was not sent.`,
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * Send a command to the watcher/server via Firestore (works from deployed dashboard).
 * Watcher executes: start, stop, kill_and_restart, run_full_automatic, stop_scraping.
 */
export async function sendLocalServerCommand(
  command: 'start' | 'stop' | 'kill_and_restart' | 'run_full_automatic' | 'stop_scraping'
): Promise<LocalServerCommandResult> {
  await ensureFirestoreAuth();
  let cmdRef;
  try {
    cmdRef = await addDoc(collection(db, 'local_server_commands'), {
      command,
      status: 'pending',
      requested_at: serverTimestamp(),
    });
  } catch (err) {
    throw formatLocalServerCommandError(err, command);
  }

  const timeoutSec = Math.round(LOCAL_SERVER_CMD_TIMEOUT_MS[command] / 1000);
  const timeoutMsg =
    command === 'start'
      ? `Watcher did not respond within ${timeoutSec}s. Ensure the watcher is running (python local_server_watcher.py or the LocalScraperWatcher scheduled task) and check watcher.log for Firestore errors.`
      : `Server did not respond to "${command}" within ${timeoutSec}s. Is local_server_watcher.py running?`;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsub();
      reject(new Error(timeoutMsg));
    }, LOCAL_SERVER_CMD_TIMEOUT_MS[command]);

    const unsub = onSnapshot(doc(db, 'local_server_commands', cmdRef.id), (snap) => {
      const data = snap.data();
      if (!data) return;
      if (data.status === 'done') {
        clearTimeout(timeoutId);
        unsub();
        resolve({
          result: (data.result as string) || 'Done',
          batch_id: (data.batch_id as string) || null,
        });
      } else if (data.status === 'error') {
        clearTimeout(timeoutId);
        unsub();
        reject(new Error(data.result || 'Command failed'));
      }
    }, (err) => {
      clearTimeout(timeoutId);
      unsub();
      reject(formatLocalServerCommandError(err, command));
    });
  }).catch((err) => {
    throw formatLocalServerCommandError(err, command);
  });
}

/**
 * Start the local scraper server via Firestore (deployed dashboard).
 * Uses the watcher; if watcher is not running, suggests running --install once.
 */
export async function startLocalServer(): Promise<{ ok: boolean; message: string; skipped?: boolean }> {
  try {
    const status = await checkLocalServerRunning();
    if (status.alive) {
      const port = status.serverPort ? ` (port ${status.serverPort})` : '';
      return { ok: true, message: `Server is already running${port}.`, skipped: true };
    }
    const { result } = await sendLocalServerCommand('start');
    return { ok: true, message: result };
  } catch (e: any) {
    const msg = e?.message || 'Failed to start';
    if (isFirestorePermissionError(e)) {
      return {
        ok: false,
        message:
          'Firestore rules are out of date (Missing or insufficient permissions). '
          + 'From repo root run: firebase deploy --only firestore:rules',
      };
    }
    return { ok: false, message: msg };
  }
}

/** Full-automatic batch doc in Firestore (`local_automation_batches/{batchId}`). */
export interface LocalAutomationBatch {
  batch_id?: string;
  source?: string;
  status?: string;
  started_at?: string;
  completed_at?: string;
  order?: string[];
  scraper_status?: Record<string, string>;
  total?: number;
  current_scraper?: string | null;
  current_run_id?: string | null;
  last_message?: string | null;
  scraper_errors?: Record<string, string>;
  lane_limits?: Record<string, number>;
  max_total_concurrent?: number;
}

export function subscribeLocalAutomationBatch(
  batchId: string | null,
  onData: (batch: LocalAutomationBatch | null) => void
): () => void {
  if (!batchId) {
    onData(null);
    return () => {};
  }
  const ref = doc(db, 'local_automation_batches', batchId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData(snap.data() as LocalAutomationBatch);
    },
    (err) => {
      console.warn('[local_automation_batches] subscribe failed:', err);
      onData(null);
    }
  );
}

/** Reconnect after refresh: most recent batch still marked running. */
export async function fetchLatestRunningAutomationBatch(): Promise<string | null> {
  try {
    const q = query(collection(db, 'local_automation_batches'), orderBy('started_at', 'desc'), limit(20));
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const data = d.data() as LocalAutomationBatch;
      if (data.status === 'running') return d.id;
    }
  } catch {
    // missing index or rules
  }
  return null;
}

export interface ScraperStatus {
  name: string;
  enabled: boolean;
  schedule_type: string;
  trigger_mode?: 'cloud_run' | 'manual_local' | 'scheduled_local';
  supports_local?: boolean;
  schedule: {
    type: string;
    day?: string;
    days?: string[];
    at: string;
    interval?: number;
  };
  priority: number;
  /** Human-readable deadline (e.g. "Every Sunday at 01:00") for local and cloud. */
  schedule_deadline?: string;
  /** ISO timestamp of last successful run; null if none. */
  last_successful_run?: string | null;
  /** True when the scraper is past due (next run was due and no successful run since). */
  is_late?: boolean;
  /** City / region label, e.g. "Tel Aviv, IL", "Haifa, IL" */
  location?: string;
}

export interface OrchestrationStatus {
  status: string;
  service_endpoint: string;
  timestamp: string;
  available_scrapers: string[];
  total_scrapers: number;
  scrapers: ScraperStatus[];
  timezone: string;
}

export interface HealthStatus {
  overall: string;
  scrapers: Record<string, {
    status: string;
    class: string;
  }>;
  timestamp: string;
}

export interface ScraperReport {
  scraper: string;
  run_id?: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  status: string;
  trigger_type?: string;
  /** URLs attempted in this run (so we can show "99 of 210 URLs") */
  total_urls?: number;
  events: {
    total: number;
    successful: number;
    duplicates: number;
    merged?: number;
    failed: number;
  };
  errors: string[];
}

export interface ReportsResponse {
  reports: ScraperReport[];
  count: number;
  timestamp: string;
}

export interface DailySummary {
  report_type: string;
  date: string;
  generated_at: string;
  total_runs: number;
  scrapers: Record<string, {
    runs: number;
    successful_runs: number;
    total_events: number;
    total_duplicates: number;
    total_failed: number;
    total_duration: number;
  }>;
  summary: {
    total_events: number;
    total_duplicates: number;
    total_merged?: number;
    total_failed: number;
    unique_scrapers: number;
    success_rate: number;
  };
}

export interface WeeklySummary {
  report_type: string;
  week_start: string;
  week_end: string;
  generated_at: string;
  summary: {
    total_runs: number;
    successful_runs: number;
    success_rate: number;
    total_events: number;
    total_duplicates: number;
    total_merged?: number;
    unique_scrapers: number;
  };
  scrapers: Record<string, {
    runs: number;
    successful_runs: number;
    total_events: number;
    total_duplicates: number;
  }>;
}

export interface ScraperDoctorRunSummary {
  recent_runs: number;
  success_rate: number | null;
  median_duration_seconds: number | null;
  last_failure_class: string | null;
}

export interface ScraperDoctorScraper {
  name: string;
  enabled?: boolean;
  priority?: number;
  location?: string;
  trigger_mode?: string;
  supports_local?: boolean;
  cloud_registered?: boolean;
  lane?: 'fast' | 'browser' | 'facebook' | string;
  needs_attention?: boolean;
  last_run_status?: string | null;
  last_run_time?: string | null;
  last_failure_class?: string | null;
  recent?: ScraperDoctorRunSummary;
}

export interface ScraperDoctorOverview {
  status: string;
  timestamp?: string;
  scrapers: ScraperDoctorScraper[];
  needs_attention_count?: number;
  lanes?: Record<string, number>;
  lane_limits?: Record<string, number>;
  max_total_concurrent?: number;
  concurrency?: {
    active_by_lane?: Record<string, number>;
    total_active?: number;
    lane_limits?: Record<string, number>;
    max_total_concurrent?: number;
  };
}

export interface ScraperDoctorReview {
  scraper: string;
  lane?: string;
  paths?: Record<string, string | null>;
  import_check?: { ok?: boolean; error?: string; scraper_classes?: string[] };
  recent?: ScraperDoctorRunSummary;
  findings?: Array<{ severity: string; kind: string; message: string }>;
  failure_classes?: string[];
  next_step?: string;
  test_queue?: LocalTriggerResult & { purpose?: string; blocked_reason?: string };
}

/**
 * Scraper keys that have a local adapter (Run locally / Firestore queue).
 * Uses the same API base as orchestration (localhost when developing, Cloud when deployed).
 */
export async function fetchLocalRunnableScrapers(): Promise<string[]> {
  let base: string;
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    const local = await getLocalApiBase();
    base = local || LOCAL_API_BASE_DEFAULT;
  } else {
    base = CLOUD_API_BASE;
  }
  try {
    const response = await fetch(`${base}/api/orchestration/local-runnable`);
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.scrapers) ? data.scrapers : [];
  } catch {
    return [];
  }
}

export async function getOrchestrationStatus(): Promise<OrchestrationStatus> {
  try {
    const response = await fetch(`${getApiBaseSync()}/api/orchestration/status`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch orchestration status (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    // Network error or CORS issue
    throw new Error(`Network error: ${error.message || 'Failed to connect to API. Check CORS settings and network connectivity.'}`);
  }
}

export async function getHealthStatus(): Promise<HealthStatus> {
  try {
    const response = await fetch(`${getApiBaseSync()}/api/orchestration/health`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch health status (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API. Check CORS settings and network connectivity.'}`);
  }
}

export async function getScraperDoctorOverview(options?: { forceLocal?: boolean }): Promise<ScraperDoctorOverview> {
  const localBase = await getLocalApiBase(options?.forceLocal === true);
  const base = localBase || getApiBaseSync();
  const response = await fetch(`${base}/api/scraper-doctor/overview`);
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`Failed to fetch Scraper Doctor overview (${response.status}): ${errorText}`);
  }
  return response.json();
}

export async function getScraperDoctorReview(
  scraperName: string,
  options?: { queueTest?: boolean }
): Promise<ScraperDoctorReview> {
  const encoded = encodeURIComponent(scraperName);
  const localBase = await getLocalApiBase(!!options?.queueTest);
  if (localBase) {
    const response = await fetch(
      `${localBase}/api/scraper-doctor/review/${encoded}${options?.queueTest ? '?run_test=true' : ''}`,
      { method: options?.queueTest ? 'POST' : 'GET' },
    );
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to ${options?.queueTest ? 'queue doctor test' : 'fetch Scraper Doctor review'} (${response.status}): ${errorText}`);
    }
    return response.json();
  }

  const response = await fetch(`${getApiBaseSync()}/api/scraper-doctor/review/${encoded}`);
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`Failed to fetch Scraper Doctor review (${response.status}): ${errorText}`);
  }
  return response.json();
}

export async function triggerScraper(
  scraperName: string,
  triggerType: 'cloud' | 'manual_local' = 'cloud',
  options?: { testMode?: boolean }
): Promise<LocalTriggerResult> {
  const testMode = options?.testMode === true;
  if (triggerType === 'manual_local') {
    // Try direct localhost first (works when dashboard runs on same machine).
    // Always re-probe ports so we don't stick to a stale instance on 8101 after restart.
    const base = await getLocalApiBase(true);
    if (base) {
      try {
        const url = `${base}/scrape-local/${scraperName}${testMode ? '?test=true' : ''}`;
        const response = await fetch(url, { method: 'POST' });
        const bodyText = await response.text();
        let body: any = {};
        try {
          body = bodyText ? JSON.parse(bodyText) : {};
        } catch {
          body = {};
        }
        if (response.status === 202 || body.status === 'queued') {
          return {
            status: 'queued',
            scraper: scraperName,
            queue_id: body.queue_id,
            message: body.message,
            already_queued: body.already_queued,
            trigger_type: 'manual_local',
            local_server: true,
          };
        }
        if (!response.ok) {
          if (body.error) {
            const runIdPart = body.run_id ? ` (Run ID: ${body.run_id})` : '';
            throw new Error(body.error + runIdPart);
          }
          throw new Error(bodyText || `Request failed with status ${response.status}`);
        }
        return {
          status: 'started',
          scraper: scraperName,
          run_id: body.run_id,
          message: body.message,
          started_at: body.started_at,
          trigger_type: 'manual_local',
          local_server: true,
          ...body,
        };
      } catch (directErr: any) {
        if (directErr?.message?.includes('already running')) throw directErr;
        console.warn('Direct localhost call failed, falling back to Firestore queue:', directErr?.message);
      }
    }
    // Fallback: queue via Firestore (works from deployed HTTPS dashboard)
    return enqueueLocalRunViaFirestore(scraperName, testMode, 'manual_local');
  }

  // Cloud trigger
  const url = `${CLOUD_API_BASE}/scrape/${scraperName}`;
  try {
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errJson = JSON.parse(errorText);
        if (errJson.error) {
          const runIdPart = errJson.run_id ? ` (Run ID: ${errJson.run_id})` : '';
          throw new Error(errJson.error + runIdPart);
        }
      } catch (e: any) {
        if (!(e instanceof SyntaxError) && e?.message) throw e;
      }
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }
    return response.json();
  } catch (error: any) {
    throw error;
  }
}

export async function getReports(
  scraper?: string, 
  limit?: number, 
  days?: number,
  startDate?: string,
  endDate?: string,
  triggerType?: 'manual' | 'scheduled' | 'manual_local' | 'scheduled_local' | 'locally' | 'all'
): Promise<ReportsResponse> {
  try {
    const params = new URLSearchParams();
    if (scraper) params.append('scraper', scraper);
    if (limit) params.append('limit', limit.toString());
    if (days) params.append('days', days.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (triggerType && triggerType !== 'all') params.append('trigger_type', triggerType);
    
    const url = `${getApiBaseSync()}/api/reports${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch reports (${response.status}): ${errorText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    // Network error or CORS issue
    throw new Error(`Network error: ${error.message || 'Failed to connect to API. Check CORS settings and network connectivity.'}`);
  }
}

export async function getLatestReports() {
  try {
    const response = await fetch(`${getApiBaseSync()}/api/reports/latest`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch latest reports (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API.'}`);
  }
}

export async function getDailySummary(date?: string): Promise<DailySummary> {
  try {
    const url = date 
      ? `${getApiBaseSync()}/api/reports/summary/daily?date=${date}`
      : `${getApiBaseSync()}/api/reports/summary/daily`;
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch daily summary (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API.'}`);
  }
}

export async function getWeeklySummary(): Promise<WeeklySummary> {
  try {
    const response = await fetch(`${getApiBaseSync()}/api/reports/summary/weekly`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch weekly summary (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API.'}`);
  }
}

/** Padding around scrape run window when opening Event List (creation-time filter). Keep in sync with report-row links. */
const EVENT_DASHBOARD_LINK_BUFFER_MS = 6 * 60 * 60 * 1000;

export function buildEventDashboardLinkState(
  source: string,
  runStart: Date,
  runEnd: Date
): { source: string; creationDateStart: string; creationDateEnd: string } {
  const buf = EVENT_DASHBOARD_LINK_BUFFER_MS;
  return {
    source,
    creationDateStart: new Date(runStart.getTime() - buf).toISOString(),
    creationDateEnd: new Date(runEnd.getTime() + buf).toISOString(),
  };
}

export interface ProgressInfo {
  scraper: string;
  run_id: string;
  started_at: string;
  status: string;
  phase: string;
  current_url_index: number;
  total_urls: number;
  events_scraped: number;
  events_uploaded: number;
  duplicates: number;
  errors_count: number;
  current_item?: string;
  last_event_title?: string;
  percent_complete: number;
  estimated_remaining_seconds?: number;
  updated_at: string;
  completed_at?: string;
  duration_seconds?: number;
}

export interface ProgressResponse {
  count: number;
  progress: Record<string, ProgressInfo>;
  timestamp: string;
}

/** Normalize a single progress row so render never sees undefined numeric fields. */
export function normalizeProgressInfo(raw: unknown, fallbackRunId = ''): ProgressInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const scraper = String(row.scraper || '');
  if (!scraper) return null;
  const runId = String(row.run_id || fallbackRunId || '');
  return {
    scraper,
    run_id: runId,
    started_at: String(row.started_at || new Date().toISOString()),
    status: String(row.status || 'running'),
    phase: String(row.phase || 'Running…'),
    current_url_index: Number(row.current_url_index) || 0,
    total_urls: Number(row.total_urls) || 0,
    events_scraped: Number(row.events_scraped) || 0,
    events_uploaded: Number(row.events_uploaded) || 0,
    duplicates: Number(row.duplicates) || 0,
    errors_count: Number(row.errors_count) || 0,
    current_item: row.current_item != null ? String(row.current_item) : undefined,
    last_event_title: row.last_event_title != null ? String(row.last_event_title) : undefined,
    percent_complete: Number(row.percent_complete) || 0,
    estimated_remaining_seconds:
      row.estimated_remaining_seconds != null ? Number(row.estimated_remaining_seconds) : undefined,
    updated_at: String(row.updated_at || new Date().toISOString()),
    completed_at: row.completed_at != null ? String(row.completed_at) : undefined,
    duration_seconds: row.duration_seconds != null ? Number(row.duration_seconds) : undefined,
  };
}

function normalizeProgressMap(raw: unknown): Record<string, ProgressInfo> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, ProgressInfo> = {};
  for (const [runId, value] of Object.entries(raw as Record<string, unknown>)) {
    const normalized = normalizeProgressInfo(value, runId);
    if (normalized) out[runId] = normalized;
  }
  return out;
}

function normalizeProgressResponse(data: unknown): ProgressResponse {
  const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const progress = normalizeProgressMap(row.progress);
  return {
    count: Number(row.count) || Object.keys(progress).length,
    progress,
    timestamp: String(row.timestamp || new Date().toISOString()),
  };
}

function parseHeartbeatActiveProgress(raw: unknown): Record<string, ProgressInfo> {
  return normalizeProgressMap(raw);
}

function buildProgressStubsFromRunningScrapers(
  running: Record<string, string> | undefined,
  existing: Record<string, ProgressInfo>,
): Record<string, ProgressInfo> {
  const out = { ...existing };
  if (!running) return out;
  for (const [scraper, runId] of Object.entries(running)) {
    if (!runId || out[runId]) continue;
    out[runId] = {
      scraper,
      run_id: runId,
      started_at: new Date().toISOString(),
      status: 'running',
      phase: 'Running on your PC — syncing live stats…',
      current_url_index: 0,
      total_urls: 0,
      events_scraped: 0,
      events_uploaded: 0,
      duplicates: 0,
      errors_count: 0,
      percent_complete: 0,
      updated_at: new Date().toISOString(),
    };
  }
  return out;
}

function parseHeartbeatProgressPayload(data: Record<string, unknown> | undefined): Record<string, ProgressInfo> {
  if (!data) return {};
  const lastSeen = data.last_seen as string | null;
  const age = lastSeen ? Date.now() - new Date(lastSeen).getTime() : Infinity;
  if (!data.alive || age >= HEARTBEAT_STALE_MS) return {};
  const parsed = parseHeartbeatActiveProgress(data.active_progress);
  return buildProgressStubsFromRunningScrapers(
    (data.running_scrapers as Record<string, string>) || {},
    parsed,
  );
}

/** Real-time local progress via Firestore heartbeat (hosted dashboard, no localhost). */
export function subscribeLocalServerProgress(
  onData: (progress: Record<string, ProgressInfo>, syncedAt?: string) => void,
): () => void {
  let unsub: (() => void) | undefined;
  void ensureFirestoreAuth()
    .then(() => {
      unsub = onSnapshot(
        doc(db, 'local_server_status', 'heartbeat'),
        (snap) => {
          if (!snap.exists()) {
            onData({});
            return;
          }
          const data = snap.data() as Record<string, unknown>;
          onData(
            parseHeartbeatProgressPayload(data),
            typeof data.progress_synced_at === 'string' ? data.progress_synced_at : undefined,
          );
        },
        (err) => {
          console.warn('[LocalProgress] heartbeat subscribe failed:', err);
          onData({});
        },
      );
    })
    .catch((err) => {
      console.warn('[LocalProgress] subscribe auth failed:', err);
      onData({});
    });
  return () => {
    unsub?.();
  };
}

/** Live progress from Firestore heartbeat — works from hosted dashboard without localhost. */
export async function fetchLocalProgressFromFirestore(): Promise<Record<string, ProgressInfo>> {
  try {
    await ensureFirestoreAuth();
    const { getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'local_server_status', 'heartbeat'));
    if (!snap.exists()) return {};
    return parseHeartbeatProgressPayload(snap.data() as Record<string, unknown>);
  } catch (err) {
    console.warn('[LocalProgress] fetchLocalProgressFromFirestore failed:', err);
    return {};
  }
}

export async function getProgress(runId?: string): Promise<ProgressResponse | ProgressInfo> {
  try {
    const url = runId 
      ? `${getApiBaseSync()}/api/progress/${runId}`
      : `${getApiBaseSync()}/api/progress`;
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to fetch progress (${response.status}): ${errorText}`);
    }
    const data = await response.json();
    // If specific run_id requested, return just the progress object
    if (runId) {
      const normalized = normalizeProgressInfo(data?.progress, runId);
      if (!normalized) {
        throw new Error(`Progress not found for run ${runId}`);
      }
      return normalized;
    }
    return normalizeProgressResponse(data);
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API.'}`);
  }
}

/** Fetch progress from the local scraper server (localhost:8101/8102/8103). Use when a local run was triggered. */
export async function getLocalProgress(runId?: string): Promise<ProgressResponse | ProgressInfo | null> {
  const fetchOnce = async (base: string): Promise<ProgressResponse | ProgressInfo | null> => {
    try {
      const url = runId ? `${base}/api/progress/${runId}` : `${base}/api/progress`;
      const response = await fetch(url);
      if (!response.ok) return null;
      const data = await response.json();
      if (runId) {
        if (!(data.found && data.progress)) return null;
        return normalizeProgressInfo(data.progress, runId);
      }
      return normalizeProgressResponse(data);
    } catch {
      return null;
    }
  };

  try {
    let base = cachedLocalApiBase || (await getLocalApiBase(false));
    if (!base) {
      base = await getLocalApiBase(true);
    }
    if (!base) {
      return null;
    }
    let data = await fetchOnce(base);
    if (data !== null) {
      return data;
    }
    // Stale port (e.g. server moved 8101→8102 after restart): drop cache and re-probe once.
    cachedLocalApiBase = null;
    base = await getLocalApiBase(true);
    if (!base) {
      return null;
    }
    return await fetchOnce(base);
  } catch {
    return null;
  }
}

export async function cancelScraper(runId: string, useLocal: boolean = false) {
  const base = useLocal ? await getLocalApiBase() : getApiBaseSync();
  try {
    const response = await fetch(`${base}/api/cancel/${runId}`, {
      method: 'POST',
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to cancel scraper (${response.status}): ${errorText}`);
    }
    return response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to API.'}`);
  }
}
